/* eslint-disable camelcase */
import React, {
  useCallback, useEffect, useState,
} from 'react';
import {
  ModalInitialQuestions, ModalLogoGeneration, TopBanner, ModalPlanHireSuccess,
} from '../components';
import { useDispatch, useSelector } from 'react-redux';
import { studioActions } from '@/redux/modules/studio';
import { ID_PRO_FEATURES, PLANS } from '../studio.enum';
import * as Styles from './StudioHandler.styles';
import ModalFeaturePro from '../components/ModalFeaturePro/ModalFeaturePro';
import HighlightFeatures from '../components/HighlightFeatures';
import { preChatActions } from '@/redux/modules';
import ProjectsBar from '../components/ProjectsBar/ProjectsBar';
import withErrorBoundary from '@/utils/errorBoundary';
import { useStudio } from '@/hooks/studio';
import LoaderPage from '@/pages/Loader/LoaderPage';
import TemplatesList from '../components/TemplatesList';
import { useHistory } from 'react-router';
import useLocale from '@/hooks/useLocale';
import { studioAnalytics } from '@/analytics/studio';
import { formatCurrency } from '@/utils/Formatters/formatCurrency';
import * as featureFlags from '@/config/GrowthBook/constants';
import { useFeatureIsOn } from '@growthbook/growthbook-react';

const StudioHandler = () => {
  const dispatch = useDispatch();
  const {
    credits, product, templates, templatesLoading, products: studioProducts, accountLoading, formCheck, documentCreationLoading,
  } = useSelector(state => state.studio);

  const [loadingProgress, setLoadingProgress] = useState(10);
  const [formAnswered, setFormAnswered] = useState(true);
  const [modalFeatureProToShow, setModalFeatureProToShow] = useState(undefined);
  const [showModalLogoGeneration, setShowModalLogoGeneration] = useState(false);
  const [showModalHireSuccess, setShowModalHireSuccess] = useState(false);

  const { routes: routesLocale } = useLocale();
  const { requestUserAccountData, requestTemplates, provisioningTrailWithoutCreditCard } = useStudio();
  const history = useHistory();
  const { location, replace } = history;

  const isProPlan = product && product.plan_type && product.plan_type !== PLANS.FREE;
  const enableInitialQuestions = useFeatureIsOn(featureFlags.ENABLE_INITIAL_QUESTIONS);

  const handlePlanHireSuccess = useCallback(() => {
    const { state } = location;
    const redirectedFrom = state && state.redirectFrom;

    if (redirectedFrom === routesLocale.fastcheckout) {
      setShowModalHireSuccess(true);
      replace(location.pathname, {});
    }
  }, [location, replace, routesLocale]);

  useEffect(() => {
    handlePlanHireSuccess();
  }, [handlePlanHireSuccess]);

  const getProducts = useCallback(() => {
    dispatch(preChatActions.productByStatus.request({ withCancelled: true, withPending: true, waitToken: true }));
  }, [dispatch]);

  const getFormCheck = useCallback(() => {
    dispatch(studioActions.formCheck.request());
  }, [dispatch]);

  const getFormAnswered = useCallback(() => {
    formCheck.answered !== undefined && setFormAnswered(formCheck.answered);
  }, [formCheck]);

  const getStudioPrices = useCallback(() => {
    if (studioProducts && !studioProducts.data) {
      dispatch(studioActions.products.request());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, studioProducts.data]);

  useEffect(() => {
    getStudioPrices();
  }, [getStudioPrices]);

  const handleCloseModalLogoGeneration = useCallback(() => {
    setShowModalLogoGeneration(false);
  }, []);

  useEffect(() => {
    getFormCheck();
  }, [getFormCheck]);

  useEffect(() => {
    getFormAnswered();
  }, [getFormAnswered]);

  useEffect(() => {
    getProducts();
  }, [getProducts]);

  useEffect(() => {
    requestUserAccountData();
  }, [requestUserAccountData]);

  useEffect(() => {
    requestTemplates();
  }, [requestTemplates]);

  const handleShowModalGetPro = useCallback(() => {
    studioAnalytics.bannerProClick();
    setModalFeatureProToShow(product && product.trial_available ? ID_PRO_FEATURES.STUDIO_PRO_OFFER : ID_PRO_FEATURES.STUDIO_PRO);
  }, [setModalFeatureProToShow, product]);

  const defineProductPrice = useCallback(() => {
    if (!studioProducts || !studioProducts.data || !studioProducts.data.studio_pro || !studioProducts.data.studio_discount) return '';

    const { data } = studioProducts;
    const { has_discount, studio_pro, studio_discount } = data;

    if (has_discount) {
      return formatCurrency(studio_discount.price);
    }

    return formatCurrency(studio_pro.price);
  }, [studioProducts]);

  const topBannerProps = useCallback(() => ({
    isProPlan,
    product,
    credits,
    trialRemainingDays: product && product.plan_type === PLANS.TRIAL && product.trial_remaining_days,
    handleShowModalGetPro,
    paidWithCreditCard: product && product.payment_card,
    planPrice: defineProductPrice(),
    loading: accountLoading,
  }), [credits, isProPlan, product, accountLoading, handleShowModalGetPro, defineProductPrice]);

  const highlightFeaturesProps = useCallback(() => ({
    isProPlan,
    credits: credits && credits.available,
    setModalFeatureProToShow,
    setShowModalLogoGeneration,
  }), [credits, isProPlan]);

  const templatesListProps = useCallback(() => ({
    isProPlan,
    templates,
    templatesLoading,
    setModalFeatureProToShow,
    handleShowModalGetPro,
  }), [isProPlan, templates, templatesLoading, handleShowModalGetPro]);

  const modalInitialQuestionsProps = useCallback(() => {
    const handleSubmit = (dataToSend) => {
      dispatch(studioActions.formSend.request(dataToSend));
    };

    return ({
      isProPlan,
      plan: product && product.plan_type,
      handleSubmit,
      setFormHasAnswered: setFormAnswered,
    });
  }, [dispatch, isProPlan, product]);

  const modalFeatureProProps = useCallback(() => {
    const productsData = studioProducts && studioProducts.data;

    const purchaseTrialWithoutCc = () => {
      const onSuccessCallback = () => {
        setModalFeatureProToShow(undefined);
        history.push(routesLocale.studio, { redirectFrom: routesLocale.fastcheckout });
      };

      provisioningTrailWithoutCreditCard(onSuccessCallback);
    };

    return {
      isProPlan,
      trialAvailable: product && product.trial_available,
      productTrial: productsData && productsData.studio_trial,
      productPro: productsData && productsData.studio_pro,
      discount: productsData && productsData.studio_discount,
      hasDiscount: productsData && productsData.has_discount,
      accountProduct: product,
      setModalFeatureProToShow,
      modalId: modalFeatureProToShow,
      renewalDate: credits && credits.renewal_date,
      planPrice: defineProductPrice(),
      purchaseTrialWithoutCc,
    };
  }, [studioProducts, credits, modalFeatureProToShow, isProPlan, product, defineProductPrice, history, provisioningTrailWithoutCreditCard, routesLocale]);

  const loadingProgressFunc = useCallback(() => {
    const interval = setInterval(() => {
      setLoadingProgress((prev) => {
        if (prev >= 85) {
          clearInterval(interval);
        }
        return prev + 5;
      });
    }, 100);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    loadingProgressFunc();
  }, [loadingProgressFunc]);

  if (!(product && product.plan_type) || documentCreationLoading) {
    return (
      <Styles.Container loading>
        <LoaderPage progress={loadingProgress} />
      </Styles.Container>
    );
  }

  const projectsBarProps = () => ({
    isProPlan,
    credits: credits && credits.available,
  });

  const modalLogoGenerationProps = () => ({
    isProPlan,
    credits: credits && credits.available,
    handleCloseModalLogoGeneration,
  });

  const modalPlanHideSuccessProps = () => ({
    closeModal: () => setShowModalHireSuccess(false),
  });

  return (
    <>
      <Styles.Container>
        <TopBanner {...topBannerProps()} />
        <HighlightFeatures {...highlightFeaturesProps()} />
        <ProjectsBar {...projectsBarProps()} />
        <TemplatesList {...templatesListProps()} />
      </Styles.Container>

      {!formAnswered && enableInitialQuestions && (
        <ModalInitialQuestions {...modalInitialQuestionsProps()} />
      )}

      {modalFeatureProToShow && (
        <ModalFeaturePro {...modalFeatureProProps()} />
      )}

      {showModalLogoGeneration && (
        <ModalLogoGeneration {...modalLogoGenerationProps()} />
      )}

      {showModalHireSuccess && (
        <ModalPlanHireSuccess {...modalPlanHideSuccessProps()} />
      )}
    </>
  );
};

export default withErrorBoundary(StudioHandler, 'StudioHandler');
